import React, { useEffect } from "react";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { SingleValue } from "react-select";
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "../../../api/useCategories";

export const useEquipmentTypeInput = (selectedEntry: { equipmentTypeId: string | null }) => {
  const equipmentTypes = useCategories("EQUIPMENT_TYPE");
  const createEquipmentType = useCreateCategory("EQUIPMENT_TYPE");
  const updateEquipmentType = useUpdateCategory("EQUIPMENT_TYPE");
  const deleteEquipmentType = useDeleteCategory("EQUIPMENT_TYPE");
  const [dirty, setDirty] = React.useState(false);
  const equipmentTypeOptions = React.useMemo(
    () => buildOptions(equipmentTypes.map(e => ({ label: e, value: e }))),
    [equipmentTypes],
  );

  const equipmentTypeDefaultValue = React.useMemo(
    () =>
      equipmentTypes
        .filter(id => selectedEntry?.equipmentTypeId === id)
        .map(id => ({ value: id, label: id }))[0] ?? null,
    [selectedEntry?.equipmentTypeId, equipmentTypes],
  );

  const [selectedEquipmentType, setSelectedEquipmentType] =
    React.useState<SingleValue<BaseOption>>(equipmentTypeDefaultValue);

  useEffect(() => {
    if (selectedEquipmentType?.value && !equipmentTypes.includes(selectedEquipmentType.value)) {
      setSelectedEquipmentType(null);
    }
  }, [selectedEquipmentType, equipmentTypes]);

  const handleEquipmentTypeChanged = React.useCallback(
    (value: SingleValue<BaseOption>) => {
      setSelectedEquipmentType(value);
      setDirty(value !== selectedEquipmentType);
    },
    [selectedEquipmentType],
  );

  const handleEquipmentTypeCreated = React.useCallback(
    async (newEquipmentType: string) => {
      await createEquipmentType(newEquipmentType);
      setSelectedEquipmentType({ value: newEquipmentType, label: newEquipmentType });
      setDirty(true);
    },
    [createEquipmentType],
  );

  const handleEquipmentTypeDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (option.value) {
        await deleteEquipmentType(option.value);
        if (selectedEquipmentType?.value === option.value) setSelectedEquipmentType(null);
        setDirty(true);
      }
    },
    [deleteEquipmentType, selectedEquipmentType],
  );

  const handleEquipmentTypeEdited = React.useCallback(
    async ({ value }: BaseOption, label: string) => {
      if (value) {
        await updateEquipmentType(value, label);
        setSelectedEquipmentType({ label, value: label });
        setDirty(true);
      }
    },
    [updateEquipmentType],
  );

  const resetValues = React.useCallback(() => {
    setSelectedEquipmentType(equipmentTypeDefaultValue);
    setDirty(false);
  }, [equipmentTypeDefaultValue]);

  return {
    equipmentTypes,
    equipmentTypeOptions,
    equipmentTypeDefaultValue,
    selectedEquipmentType,
    dirty,
    resetValues,
    handleEquipmentTypeChanged,
    handleEquipmentTypeCreated,
    handleEquipmentTypeDeleted,
    handleEquipmentTypeEdited,
  };
};
